import React, { useEffect } from "react";
import { helmetJsonLdProp } from "react-schemaorg";

import { Container, Row, Col } from "react-bootstrap/esm/index";
import Helmet from "react-helmet";
import { pushContactClick } from "../../assets/datalayerFunctions";

// Import Components
import ITPHeroHeader from "../../components/HeroHeader/ITPHeroHeader";
import ItpNavigation from "../../components/ITPNavigation/ITPNavigation";
import Accordion from "../../components/shared-ui/Accordion/Accordion";
import FooterITP from "../../components/shared-ui/Footer/Footer-ITP";
import BackToTopBtn from "../../components/shared-ui/BackToTopBtn/BackToTopBtn";
import ISIITPinline from "../../components/shared-ui/ISI/ISIITP-inline";
import ISIwrapper from "../../components/shared-ui/ISI/ISI-wrapper";
import ISIITP from "../../components/shared-ui/ISI/ISIITP";
import LookingForSupport from "../../components/shared-ui/Cards/LookingForSupport";

// Import Styles
import "../../css/FinancialAssistance.scss";
import PrimaryButton from "../../components/shared-ui/Buttons/PrimaryButton";

function showTermsModal(event) {
  event.preventDefault();
  const modal = document.getElementById("terms-modal");
  document.body.classList.add("no-scroll");
  modal.classList.remove("fadeOut");
  modal.style.display = "block";
  modal.classList.add("fadeIn");
  modal.style.height = "100vh";
  if (
    event.length &&
    event.currentTarget.classList.contains("scroll-to-terms")
  ) {
    const terms = document.getElementById("terms");
    const container = document.querySelector("#terms-modal .modal-container");
    container.scroll(0, terms.offsetTop);
  }
}

function showTermsModalSimple() {
  const modal = document.getElementById("terms-modal");
  document.body.classList.add("no-scroll");
  modal.classList.remove("fadeOut");
  modal.style.display = "block";
  modal.classList.add("fadeIn");
  modal.style.height = "100vh";
}

function FinancialAssistance() {
  useEffect(() => {
    window.scrollTo(0, 0);
    let location = window.location.pathname;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      page: {
        path: `${location}`,
        title: "Doptelet Copay Assistance Program | DOPTELET® (avatrombopag)",
        environment: "dev",
        indication: "ITP",
        audience: "DTC",
      },
    });
    if (window.location.search.includes("terms-modal")) {
      showTermsModalSimple();
    }
  }, []);
  return (
    <div className="main_content_wrap">
      <Helmet
        script={[
          helmetJsonLdProp({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Drug",
                name: "Doptelet",
                alternateName: "avatrombopag",
                manufacturer: {
                  "@type": "Organization",
                  name: "Sobi",
                },
                dosageForm: "Pill",
              },
              {
                "@type": "WebSite",
                about: {
                  "@type": "Thing",
                  description:
                    "To inform patients of Doptelet (avatrombopag), a prescription medicine used to treat low blood platelet counts in adults with chronic immune thrombocytopenia when other treatments have not worked well enough. Doptelet (avatrombopag) is also a prescription medicine used to treat low blood platelet counts in adults with long-lasting (chronic) liver disease who are scheduled to have a medical or dental procedure.",
                },
                url: "https://doptelethcp.com/itp/doptelet-copay/",
              },
              {
                "@type": "Brand",
                logo: "https://doptelet.com/images/doptelet-avatrombopag-tablets-logo.svg",
              },
              {
                "@type": "Brand",
                logo: "https://doptelet.com/images/sobi-logo.png",
              },
              {
                "@type": "MedicalAudience",
                audienceType:
                  "Patients with chronic immune thrombocytopenia when other treatments have not worked well enough. ",
                healthCondition: {
                  "@type": "MedicalCondition",
                  alternateName: "Immune Thrombocytopenia (ITP)",
                },
                requiredMinAge: "18",
              },
              {
                "@type": "HealthTopicContent",
                hasHealthAspect:
                  "The most common side effects of DOPTELET when used to treat low blood platelet counts in adults with chronic immune thrombocytopenia (ITP) are: headache, tiredness, bruising, nosebleed, upper-respiratory tract infection, joint pain, gum bleeding, purple or red spots on your skin, runny nose",
              },
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  {
                    "@type": "ListItem",
                    position: 3,
                    item: {
                      "@id":
                        "https://doptelet.com/support-and-resources/financial-assistance/",
                      Name: "Find ITP Resources",
                    },
                  },
                ],
              },
            ],
          }),
        ]}
      >
        <title>
          Patient Financial Assistance, Copay Card & Trial Offer | DOPTELET®
          (avatrombopag)
        </title>
        <meta
          name="description"
          content="Discover financial assistance options to help manage the cost of chronic ITP treatment, like the Doptelet® copay card, free trial offer & more. Terms and conditions apply."
        />
        <link
          rel="canonical"
          href="https://doptelet.com/support-and-resources/financial-assistance/"
        />
      </Helmet>
      <ItpNavigation />
      <div className="inner-content-wrap financial">
        <ITPHeroHeader
          extraClass="large-hero inner-bg"
          markup={
            <>
              <h1>FINANCIAL ASSISTANCE INFORMATION</h1>
              <p className="mb-0">
                Explore all of the Doptelet financial assistance options that may be available. The Doptelet Copay Assistance Program and Free Trial Offer are here to help manage access to Doptelet.
              </p>
            </>
          }
          maxWidth="513"
          bgImage={[
            "financial-assistance-info-hero.jpg",
            "financial-assistance-info-hero@2x.jpg",
          ]}
          bgImageMobile={[
            "financial-assistance-info-hero-mobile.jpg",
            "financial-assistance-info-hero-mobile@2x.jpg",
          ]}
          imageAlt="Doptelet platelet characters holding colorful balloons in carnival scenery"
        />

        <section className="section-padding doptelet-connect-programs">
          <Container>
            <Row className="mb-5">
              <Col md={12} lg={3} className="mb-4 mb-lg-0">
                <img
                  src="/images/pay-as-little-sign.svg"
                  alt="Pay as little as zero dollars icon representing Doptelet copay assistance program"
                  className="d-block m-auto"
                  style={{ maxHeight: 200 }}
                />
              </Col>
              <Col md={12} lg={9}>
                <h2 className="fz25 mb-3">Enroll in the Doptelet Copay Assistance Program</h2>
                <p>
                  The Doptelet Copay Assistance Program is for eligible people who have commercial prescription insurance. If you qualify, you’ll be able to pay as little as $0 per prescription, and may receive up to a maximum benefit of $15,000 per calendar year. <a onClick={showTermsModal} className="scroll-to-terms" href="#terms">Terms and conditions apply</a>.
                </p>
                <PrimaryButton
                  title="Enroll Now"
                  href="https://sobipatientsupport.iassist.com/patient/doptelet/copay-assist"
                  target="_blank"
                  addClass="btn-primary btn-white btn-external-link"
                />
              </Col>
            </Row>
            <Row className="align-items-center">
              <Col md={12} lg={3} className="mb-4 mb-lg-0">
                <img
                  src="/images/free-trial-offer-sign.svg"
                  alt="Free trial offer icon"
                  className="d-block m-auto"
                  style={{ maxHeight: 200, maxWidth: 250 }}
                />
              </Col>
              <Col md={12} lg={9}>
                <h2 className="fz25 mb-3">Learn about the Doptelet Free Trial Offer</h2>
                <p>
                  The Free Trial Offer provides a 15-day supply of Doptelet, at no cost, to patients who are new to Doptelet and have chronic ITP. The Free Trial Offer allows physicians and patients to evaluate a patient’s clinical response to Doptelet to determine whether it is the appropriate treatment. If you are curious if Doptelet will work for you, discuss the Doptelet Free Trial Offer with your healthcare provider. You can find the form to request the Free Trial Offer (to be filled out by your doctor) below. Your provider may also request the Free Trial Offer online at DopteletConnectHCP.com.{" "}
                  <a
                    style={{ margin: "0" }}
                    onClick={showTermsModal}
                    href="#terms"
                  >
                    Terms and conditions apply
                  </a>
                  .
                </p>
                <PrimaryButton
                  title="DOWNLOAD FORM"
                  href="/pdf/NP-39527_Doptelet_Free_Trial_Offer_Program_Request_Form_Digital_FINAL_2.11.25.pdf"
                  addClass="btn btn-primary download download-white btn-transparent"
                  target="_blank"
                />
              </Col>
            </Row>
          </Container>
        </section>

        <section className="section-padding cloud-blue-bg doptelet-connect">
          <Container>
            <Row>
              <Col>
                <div className="doptelet-connect-header">
                  <div className="mb-3">
                    <img src="/images/doptelet-connect.svg" alt="Doptelet Connect logo" />
                  </div>
                  <div className="">
                    <h2>
                      After your doctor prescribes Doptelet, Doptelet Connect can provide patient assistance options that may help you access treatment
                    </h2>
                    <h3>There are 3 ways to get support from Doptelet Connect:</h3>
                  </div>
                </div>
                <div className="doptelet-connect-cards">
                  <div className="doptelet-connect-card">
                    <h3>Doptelet Connect Secure Online Portal</h3>
                    <p>
                      Go online to complete the Patient Consent Form and provide the necessary information. From there, you can access the portal to check your eligibility for Doptelet copay assistance or upload information requested by Doptelet Connect.
                    </p>
                    <a
                      href="https://sobipatientsupport.iassist.com/patient/doptelet/"
                      className="btn btn-white btn-primary btn-external-link"
                      target="_blank"
                    >
                      <span>Enroll</span>
                    </a>
                  </div>
                  <div className="doptelet-connect-card">
                    <h3>Prescription and Enrollment Form</h3>
                    <p>
                      To enroll in Doptelet Connect, your doctor will need to complete the form, including all healthcare provider and patient signatures, and fax it to{" "}
                      <a href="tel:18556868729">
                        1-855-686-8729
                      </a>
                      .
                    </p>
                    <a
                      href="https://doptelet.com/pdf/doptelet-prescription-enrollment-form.pdf"
                      className="btn btn-white btn-primary download download-white"
                    >
                      <span style={{ minHeight: "30px" }}>Download</span>
                    </a>
                  </div>
                  <div className="doptelet-connect-card">
                    <h3>Call Doptelet Connect</h3>
                    <p>
                      To ask questions and get direct assistance, call{" "}
                      <a href="tel:18333682663">
                        1-833-368-2663
                      </a>
                      .
                    </p>
                    <a
                      href="tel:18333682663"
                      className="btn btn-white btn-primary btn-external-link"
                    >
                      <span>Call</span>
                    </a>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        <section className="section-padding case-manager">
          <Container>
            <Row>
              <Col>
                <div className="case-manager-content">

                  <div className="">
                    <Row>
                      <div className="case-manager-content-image col-lg-3 col-md-12">
                        <img src="/images/case-manager-sign.svg" alt="Case Manager Sign" />
                      </div>
                      <div className="col-lg-9 col-md-12">
                        <h2>Connect with Case Managers</h2>
                        <h3>Here’s how it works:</h3>
                      </div>
                    </Row>

                    <div className="case-manager-content-cards">
                      <div className="case-manager-content-card card1">
                        <div>
                          <img src="/images/icon-prescription.svg" alt="Paper with pen icon representing Doptelet prescription or patient enrollment forms" className="mb-3 mb-md-0 mr-md-4" />
                        </div>
                        <div>
                          <h4>
                            First, if your doctor’s office chooses, they can send your prescription to Doptelet Connect for review.
                          </h4>
                        </div>
                      </div>
                      <div className="case-manager-content-card card2">
                        <div>
                          <img src="/images/icon-representative.svg" alt="Doptelet® Dedicated Care Coordinators" className="mb-3 mb-md-0 mr-md-4" />
                        </div>
                        <div>
                          <h4>Next, dedicated Case Managers will help:</h4>
                          <ul>
                            <li>
                              <strong>Research</strong> your insurance plan
                            </li>
                            <li>
                              <strong>Determine</strong> a specialty pharmacy that can fill and deliver your Doptelet prescription
                            </li>
                            <li>
                              <strong>Identify</strong> potential financial assistance
                              options that may be available
                            </li>
                          </ul>
                          <p>
                            <strong>
                              For more information, call{" "}
                              <a href="tel:18333682663">
                                1-833-368-2663
                              </a>
                              . Case Managers are available Monday–Friday 8:30 AM–7 PM
                              ET.
                            </strong>
                          </p>
                        </div>
                      </div>
                      <div className="case-manager-content-card card3">
                        <div>
                          <img src="/images/icon-pharmacy.svg" alt="Pharmacy RX icon" className="mb-3 mb-md-0 mr-md-4" />
                        </div>
                        <div>
                          <h4>
                            Finally, a pharmacy will contact you to confirm your delivery details before sending you Doptelet. It is important to answer or return calls from the specialty pharmacy right away.
                          </h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </section>



        <section className="section-padding cloud-blue-bg patient-resources">
          <Container>
            <Row>
              <Col>
                <h2>Doptelet Connect resources</h2>
              </Col>
            </Row>
            <Row>
              <Col md={12} lg={4} className="mb-4 mb-lg-0">
                <div className="resource">
                  <div className="d-flex align-items-center mb-2">
                    <img
                      src="/images/icon-prescription.svg"
                      alt="Paper with pen icon representing Doptelet prescription or patient enrollment forms"
                      className="mr-3"
                      style={{ maxWidth: 64 }}
                    />
                    <h3 className="mb-0">
                      <strong>Prescription and Enrollment Form</strong>
                    </h3>
                  </div>
                  <p className="flex-grow-1">
                    To enroll in Doptelet Connect, work with your healthcare
                    provider to complete and sign this form.
                  </p>
                  <PrimaryButton
                    title="DOWNLOAD FORM"
                    href="/pdf/NP-38058_Doptelet_HUB_PST_US_Prescription_and_Enrollment_Form_Digital.pdf"
                    addClass="btn btn-primary download w-100 mb-2"
                    target="_blank"
                  />
                </div>
              </Col>
              <Col md={12} lg={4} className="mb-4 mb-lg-0">
                <div className="resource">
                  <div className="d-flex align-items-center mb-2">
                    <img
                      src="/images/icon-prescription.svg"
                      alt="Paper with pen icon representing Doptelet prescription or patient enrollment forms"
                      className="mr-3"
                      style={{ maxWidth: 64 }}
                    />
                    <h3 className="mb-0">
                      <strong>Patient/Caregiver Consent Form</strong>
                    </h3>
                  </div>
                  <p className="flex-grow-1">
                    Complete this form if your signature is requested by
                    Doptelet Connect.
                  </p>
                  <PrimaryButton
                    title="DOWNLOAD FORM"
                    href="/pdf/NP-38062_Doptelet_HUB_PST_US_Doptelet_Patient-Authorized_Representative_Consent_Form-DIGITAL.pdf"
                    addClass="btn btn-primary download w-100 mb-2"
                    target="_blank"
                  />
                  <PrimaryButton
                    title="Descargar en español"
                    href="/pdf/NP-28713_Doptelet_Spanish_Consent_Form_Digital_FINAL_7.11.23.pdf"
                    addClass="btn btn-primary download w-100 mb-2"
                    target="_blank"
                  />
                </div >
              </Col >
              <Col md={12} lg={4}>
                <div className="resource">
                  <div className="d-flex align-items-center mb-2">
                    <img
                      src="/images/icon-prescription.svg"
                      alt="Paper with pen icon representing Doptelet prescription or patient enrollment forms"
                      className="mr-3"
                      style={{ maxWidth: 64 }}
                    />
                    <h3 className="mb-0"><strong>Patient Assistance Program Application</strong></h3>
                  </div>
                  <p className="flex-grow-1">To be evaluated for the Patient Assistance Program, work with your healthcare provider to complete and sign this application.</p>
                  <PrimaryButton
                    title="DOWNLOAD FORM"
                    href="/pdf/NP-38064_Doptelet_HUB_PST_US_PAP_Application_Digital.pdf"
                    addClass="btn btn-primary download w-100 mb-2"
                    target="_blank"
                  />
                </div >
              </Col >
            </Row >
          </Container >
        </section >

        <section className="section-padding">
          <Container>
            <Row>
              <Col>
                <h2 className="mb-4">Discover the Doptelet Copay Assistance Program</h2>
                <p className="mb-4 text-blue"><strong>Eligible commercially insured patients may qualify for the Doptelet Copay Assistance Program</strong></p>
                <Accordion
                  title="What’s the program?"
                  id="whats-the-program"
                  className="scroll-to-section"
                  markup={
                    <>
                      <ul>
                        <li>Eligible patients may pay as little as $0 for each Doptelet prescription</li>
                        <li>Eligible patients may receive up to a maximum benefit of $15,000 per calendar year</li>
                      </ul >
                    </>
                  }
                />
                < Accordion
                  title="Eligibility requirements"
                  id="eligibility-requirements"
                  className="scroll-to-section"
                  markup={
                    <>
                      <p className="text-blue"><strong>Eligible patients must:</strong></p>
                      <ul>
                        <li>Have commercial prescription insurance that covers Doptelet</li>
                        <li>The Program is not valid for patients whose prescription claims are reimbursed or paid for, in whole or in part, by any state or federal government program, including, but not limited to Medicaid, Medicare, Medigap, Department of Defense (DoD), Veterans Affairs (VA), TRICARE, Puerto Rico Government Insurance, or any state patient or pharmaceutical assistance program</li>
                        <li>
                          Be 1 year of age or older
                        </li>
                        <li>Be a resident of the United States or Puerto Rico</li>
                      </ul>
                    </>
                  }
                />
                < Accordion
                  title="How to enroll"
                  id="how-to-enroll"
                  className="scroll-to-section"
                  markup={
                    <>
                      <ul>
                        <li>Approved pharmacies, healthcare professionals, and Doptelet Connect can enroll patients</li>
                        <li>Patients can enroll themselves or be enrolled by their caregivers</li>
                        <li>Please call <a
                          href="tel:+18333682663"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="no-break-mobile text-bold"
                          onClick={pushContactClick}>
                          1&#45;833&#45;368&#45;2663
                        </a> Monday–Friday 8:30 AM–7 PM ET for more information</li>
                      </ul >
                    </>
                  }
                />
                < Accordion
                  title="Terms and conditions"
                  id="terms-and-conditions"
                  className="scroll-to-section mb-0"
                  markup={
                    <>
                      <ul>
                        <li>
                          Patients may pay as little as $0 per prescription.{" "}
                          Copay Assistance Program
                          has an annual calendar cap of $15,000
                        </li>
                        <li>
                          The Copay Assistance Program is void where prohibited by
                          law, taxes, or restricted
                        </li>
                        <li>
                          This offer is non-transferable, no substitutions are
                          permissible, and this offer cannot be combined with any
                          other rebate/coupon, free trial, or similar offer for the
                          specified prescription
                        </li>
                        <li>
                          Sobi, Inc. reserves the right to rescind, revoke, or amend
                          this offer at any time without notice
                        </li>
                        <li>This offer is not valid for patients paying with cash</li>
                        <li>
                          The Copay Assistance Program for Doptelet is not insurance
                          and is not intended to substitute for insurance
                        </li>
                        <li>
                          Patients, pharmacists, and healthcare providers must not
                          seek reimbursement from health insurance or any third party
                          for any part of the benefit received by the patient through
                          this Copay Assistance Program. Patients must not seek
                          reimbursement from any health savings, flexible spending, or
                          other healthcare reimbursement accounts for the amount of
                          assistance received from the Copay Assistance Program
                        </li>
                        <li>
                          Enrollment for the Copay Assistance Program is valid through
                          the calendar year
                        </li>
                        <li>
                          Sobi, Inc. reserves the right to change program terms at any
                          time
                        </li>
                        <li>
                          The Program is not valid for claims or transactions submitted more than 90 days from the date the prescription is filled</li>
                        <li>
                          The Program covers the cost of the drug only and does not cover costs for office visits or any other associated costs
                        </li>
                      </ul>
                    </>
                  }
                />
                <h2 style={{ marginTop: "40px" }}>
                  See the Free Trial Offer terms and conditions
                </h2>
                <Accordion
                  title="Terms and conditions"
                  id="more-results"
                  className="scroll-to-section mb-0"
                  markup={
                    <>
                      <ul>
                        <li>
                          In order to participate in the Doptelet Free Trial Offer, a
                          patient must be new to Doptelet therapy and be aged ≥1 year
                          for pediatric patients and &gt;18 years of age for adults. The
                          patient must be prescribed Doptelet for an approved on-label
                          indication (ITP only) and be under the care of a licensed
                          healthcare provider who is authorized to prescribe medicine in
                          the United States or US Territories, and the patient must
                          reside in the United States or US Territories. Additionally, a
                          patient can only participate in the Doptelet Free Trial Offer
                          once. When prescribed Doptelet Sprinkle under the Free Trial
                          Offer, due to how Doptelet Sprinkle is supplied, eligible
                          patients will receive a bottle with 30 capsules. The offer is
                          void where prohibited by law. Certain rules and restrictions
                          apply. Sobi reserves the right to revoke, rescind, or amend
                          this offer without notice. For ages 1-6, subject to availability for Doptelet Sprinkle
                        </li>
                      </ul>

                    </>
                  }
                />
              </Col >
            </Row >
          </Container >
        </section >

        <LookingForSupport addClass="section-padding pt-0" />

        <ISIITPinline />
      </div >
      <FooterITP pp="PP-28475 07/25" />
      <ISIwrapper>
        <ISIITP />
      </ISIwrapper>
      <BackToTopBtn />
    </div >
  );
}

export default FinancialAssistance;
